// hooks.js - The four hooks that carry nearly every screen. Anything more
// specific belongs next to the component that needs it.
//
// LEGEND
//   1. useAsync()      run a promise on mount, get { data, error, loading, refreshing, reload }
//   2. useForm()       controlled values plus server field errors
//   3. useSpot()       live metal prices, shared and refreshed on an interval
//   4. useDebounced()  debounce a value, for search boxes and live pricing
//   5. useLocal()      a piece of state mirrored into localStorage

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { publicApi, ApiError } from "./api.js";

////////// 1. ASYNC //////////
// `deps` behaves like useEffect's. The request is aborted on unmount and on any
// dependency change, so a fast click through a list never renders stale data.
//
// `loading` means "there is nothing to show yet", not "a request is in flight".
// The difference is the whole reason this hook is worth having. Callers write
// `if (loading) return <Loading />`, so a `loading` that went true on every
// background refresh replaced the whole page with a spinner every time a poll
// came back, which is what the order page did every twelve seconds. The data
// was never gone; the page threw it away and drew it again.
//
// `refreshing` is the other half, for a caller that wants to show a quiet hint
// that something is being fetched without taking the page away.
//
// Reloading and changing the subject are different things. `reload()` is about
// the same thing, so what is on screen stays there while the new copy arrives.
// A change of deps is about something else, so the old data goes: showing order
// A's numbers under order B's heading for half a second is worse than a spinner.

export function useAsync(fn, deps = [], { skip = false } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: !skip, refreshing: false });
  const [nonce, setNonce] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // Every caller passes primitives, so this is a safe identity for "the same
  // subject". It is compared rather than spread into the dependency list so a
  // reload can be told apart from a change of subject.
  const subject = deps.map((value) => String(value)).join("\u0000");
  const owner = useRef(null);

  useEffect(() => {
    if (skip) {
      owner.current = null;
      setState({ data: null, error: null, loading: false, refreshing: false });
      return undefined;
    }
    const controller = new AbortController();
    let alive = true;

    const changed = owner.current !== subject;
    owner.current = subject;

    setState((previous) => ({
      ...previous,
      data: changed ? null : previous.data,
      loading: changed || previous.data === null,
      refreshing: true,
      error: null,
    }));
    Promise.resolve(fnRef.current(controller.signal))
      .then((data) => { if (alive) setState({ data, error: null, loading: false, refreshing: false }); })
      .catch((error) => {
        if (!alive || error.name === "AbortError") return;
        // A failed background refresh keeps what is on screen. Replacing a page
        // that is working with an error because one poll timed out is the same
        // mistake as the spinner, in a worse colour.
        setState((previous) => ({
          data: previous.data,
          error: previous.data === null ? error : null,
          loading: false,
          refreshing: false,
        }));
      });

    return () => { alive = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, nonce, skip]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const setData = useCallback((updater) => {
    setState((previous) => ({ ...previous, data: typeof updater === "function" ? updater(previous.data) : updater }));
  }, []);

  return { ...state, reload, setData };
}

////////// 2. FORMS //////////
// Values, per field errors from the server, and a submit wrapper that maps an
// ApiError back onto the fields it came from.

export function useForm(initial = {}) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = useCallback((name, value) => {
    setValues((previous) => {
      // Dotted names write into nested objects, which is what the API expects
      // for things like customer.address.city.
      if (!name.includes(".")) return { ...previous, [name]: value };
      const [head, ...rest] = name.split(".");
      const nested = { ...(previous[head] || {}) };
      let cursor = nested;
      for (let index = 0; index < rest.length - 1; index += 1) {
        cursor[rest[index]] = { ...(cursor[rest[index]] || {}) };
        cursor = cursor[rest[index]];
      }
      cursor[rest.at(-1)] = value;
      return { ...previous, [head]: nested };
    });
    setErrors((previous) => (previous[name] ? { ...previous, [name]: undefined } : previous));
  }, []);

  const field = useCallback((name) => ({
    name,
    value: name.split(".").reduce((cursor, key) => cursor?.[key], values) ?? "",
    onChange: (event) => set(name, event?.target ? (event.target.type === "checkbox" ? event.target.checked : event.target.value) : event),
    error: errors[name],
  }), [values, errors, set]);

  const submit = useCallback((handler) => async (event) => {
    event?.preventDefault?.();
    setBusy(true);
    setMessage(null);
    setErrors({});
    try {
      const result = await handler(values);
      return result;
    } catch (error) {
      if (error instanceof ApiError) {
        setErrors(error.fields || {});
        setMessage({ kind: "error", text: error.message });
      } else {
        setMessage({ kind: "error", text: error.message || "Something went wrong" });
      }
      return null;
    } finally {
      setBusy(false);
    }
  }, [values]);

  return { values, setValues, set, field, errors, setErrors, message, setMessage, busy, submit };
}

////////// 3. SPOT PRICES //////////
// Shared across every component that mounts it, so a page with a ticker and two
// calculators still makes one request.

let spotCache = { data: null, at: 0 };
const spotListeners = new Set();

async function loadSpot(force = false) {
  if (!force && spotCache.data && Date.now() - spotCache.at < 60_000) return spotCache.data;
  const data = await publicApi.spot();
  spotCache = { data, at: Date.now() };
  for (const listener of spotListeners) listener(data);
  return data;
}

export function useSpot({ refreshMs = 120_000 } = {}) {
  const [spot, setSpot] = useState(spotCache.data);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const listener = (data) => { if (alive) setSpot(data); };
    spotListeners.add(listener);

    loadSpot().then(listener).catch((problem) => alive && setError(problem));
    const timer = setInterval(() => { loadSpot(true).catch(() => {}); }, refreshMs);

    return () => { alive = false; spotListeners.delete(listener); clearInterval(timer); };
  }, [refreshMs]);

  return { spot, error, loading: !spot && !error };
}

////////// 4. DEBOUNCE //////////

export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

////////// 5. LOCAL STORAGE //////////
// Wrapped in try/catch because private windows and locked down browsers throw on
// access rather than returning null.

// Only for a value that is read as well as written, and only for one whose
// identity is stable: a string, a number, a boolean. Passing it a fresh object
// every render is an infinite loop, because the write is an effect and the
// effect changes the state that causes the next render. Something that is only
// ever written belongs in localStorage directly, which is what useDraft in
// RequestKit.jsx does after that loop cost a working submit button.
export function useLocal(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* not available */ }
  }, [key, value]);

  return [value, setValue];
}

////////// UTILITY //////////

export function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export const useIsomorphicId = () => useMemo(() => `id-${Math.random().toString(36).slice(2, 9)}`, []);
